/**
 * Firebase — PRODUCTION only.
 *
 * The Firebase *web* config is public (Google secures it via app/domain rules,
 * not secrecy) and lives in `.env.production`. Everything Firebase is loaded via
 * dynamic import, so the SDK never lands in the eager bundle and is a complete
 * no-op in development. Used by web push (`@/lib/webPush`).
 *
 * Firebase Analytics is intentionally NOT initialized here: it reports to the
 * same GA4 property (`G-R6T2Y2397W`) as the gtag.js tag in `index.html`, and
 * configuring one measurement ID twice double-counts page views.
 */
import type { FirebaseApp } from 'firebase/app';

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let appPromise: Promise<FirebaseApp | null> | null = null;

/**
 * Lazily initialize (once) and return the default Firebase app — or `null` in
 * dev or when the config is absent. Never throws.
 */
export function getFirebaseApp(): Promise<FirebaseApp | null> {
  if (!import.meta.env.PROD) return Promise.resolve(null);
  if (!firebaseConfig.apiKey || !firebaseConfig.appId) return Promise.resolve(null);
  if (!appPromise) {
    appPromise = import('firebase/app')
      .then(({ initializeApp, getApps, getApp }) =>
        getApps().length ? getApp() : initializeApp(firebaseConfig),
      )
      .catch(() => null);
  }
  return appPromise;
}
